<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { SwitchRoot, SwitchThumb } from "reka-ui";

import { caseService } from "@/services";
import { classifyApiError } from "@/services/Api";

import { useFiltersStore } from "@/stores/filters-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import Loading from "@/components/Loading.vue";
import SummaryItem from "@/components/CasePanels/Summary/SummaryItem.vue";

import type { Snapshot } from "@/models/Annotation";
import type { FindCitation } from "@/models/FindAsk";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const routeParams = useCaseRouteParams();

const router = useRouter();

const findQuery = ref("");
const isAskFindType = ref(false);
const allFilteredRecords = ref(true);

type FindResults = ({ type: "search"; response: undefined } | { type: "ask"; response: string }) & {
  snapshots: Snapshot[];
};

const state = ref<
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "done"; results: FindResults }
>({ status: "idle" });

const filteredResults = computed<Snapshot[]>(() => {
  if (state.value.status !== "done") return [];

  if (allFilteredRecords.value) {
    const files = useMetadataStore().filteredFilesInCurrentFolder.map((file) => file.fileReference);
    return state.value.results.snapshots.filter((res) => files.includes(res.fileReference));
  }

  return state.value.results.snapshots.filter((res) => res.fileReference === routeParams.value.casefile);
});

const MAX_QUERY_WORD_COUNT = 15;
const queryEl = ref<HTMLTextAreaElement>();
const queryWordCount = computed(() => (findQuery.value.length === 0 ? 0 : findQuery.value.split(/\s+/).length));

function resetFilters() {
  useFiltersStore().reset();
  allFilteredRecords.value = true;
}

async function runFind() {
  try {
    state.value = { status: "loading" };

    const { client, brand, casename } = routeParams.value;

    let results: FindResults;

    if (isAskFindType.value) {
      const askResponse = await caseService.caseAsk(client, brand, casename, findQuery.value);
      results = {
        type: "ask",
        response: askResponse.response,
        snapshots: getSnapshots(
          askResponse.datapoints.map((dp) => ({
            ...dp,
            citations: dp.citations?.map((citation) => ({ ...citation, type: citation.subType }) as FindCitation),
          })),
        ),
      };
    } else {
      const findResponse = await caseService.caseFind(client, brand, casename, findQuery.value);
      results = { type: "search", response: undefined, snapshots: getSnapshots(findResponse) };
    }

    state.value = { status: "done", results };
  } catch (error) {
    const result = classifyApiError("Find", error);
    if (result.type === "recovered") state.value = { status: "idle" };
    else if (result.severity === "unknown") state.value = { status: "error", message: "Unknown Error!" };
    else state.value = { status: "error", message: result.message };
  }
}

function getSnapshots(dataPoints: Array<{ fileReference: string; citations: FindCitation[] | null | undefined }>) {
  return dataPoints.flatMap(({ citations, fileReference }) => {
    if (citations == null) return [];

    return citations.flatMap<Snapshot>((citation) => {
      const uid = Math.random().toString(36);
      const base = {
        uid,
        fileReference,
        bookmarks: [],
        associatedThemes: [],
        status: "ai" as const,
      };

      switch (citation.type) {
        case "cdm":
          return {
            ...base,
            type: "CDMAnnotation",
            text: citation.text,
            rectangle: citation.rectangle,
            page: { number: citation.page, height: citation.pageHeight, width: citation.pageWidth },
          };
        case "audio":
          return {
            ...base,
            type: "TranscriptAnnotation",
            startTime: citation.startTime,
            endTime: citation.endTime,
            utteranceList: [
              { order: 0, dialogueText: citation.dialogueText, isContext: false, speaker: citation.speaker },
            ],
          };
        case "transcript":
          if (citation.supporting == null) return [];

          return {
            ...base,
            type: "TranscriptAnnotation",
            startTime: citation.contentJson.startTime,
            endTime: citation.contentJson.endTime,
            utteranceList: citation.contentJson.utteranceList,
          };
      }
    });
  });
}

function goToCitation(item: Snapshot) {
  const params = { brand: routeParams.value.brand, casename: routeParams.value.casename, casefile: item.fileReference };

  if (item.type === "CDMAnnotation") {
    useAnnotationStore().selectArea(
      { page: item.page, rectangle: item.rectangle, fill: "#E5DEDE", opacity: 0.4 },
      params.casefile,
    );
    router.push({ name: "casefilepage", params: { ...params, filepage: item.page.number }, force: true });
  } else if (item.type === "TranscriptAnnotation") {
    const { startTime, endTime } = item;
    router.push({ name: "casefiletimerange", params: { ...params, startTime, endTime }, force: true });
  }
}

function resetFind() {
  findQuery.value = "";
  state.value = { status: "idle" };
}

watch(queryWordCount, () => {
  if (queryEl.value == null) return;

  queryEl.value.setCustomValidity(
    queryWordCount.value > MAX_QUERY_WORD_COUNT
      ? `Please shorten this text to ${MAX_QUERY_WORD_COUNT} words or less (you are currently using ${queryWordCount.value}).`
      : "",
  );
});
</script>
<template>
  <div class="tw-flex tw-size-full tw-h-full tw-flex-col tw-gap-y-1.5 tw-overflow-y-auto">
    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-bg-gray-800 tw-p-1">
      <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-p-1 focus-within:tw-ring">
        <label for="findType" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">Search</label>
        <SwitchRoot
          id="findType"
          v-model="isAskFindType"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
        <label for="findType" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">Ask</label>
      </div>
      <div aria-hidden="true" class="tw-w-px tw-bg-white tw-py-1.5" />
      <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-p-1 focus-within:tw-ring">
        <label for="findFilteredRecords" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">
          All Filtered Records
        </label>
        <SwitchRoot
          id="findFilteredRecords"
          v-model="allFilteredRecords"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
          :disabled="routeParams.casefile == null"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
      </div>
    </div>
    <form
      class="tw-grid tw-grid-cols-[minmax(0,1fr)_max-content] tw-grid-rows-2 tw-gap-2 tw-px-3"
      @submit.prevent="runFind"
    >
      <div class="tw-row-span-full tw-flex tw-size-full tw-flex-col tw-space-y-0.5">
        <div class="tw-flex tw-items-center tw-justify-between">
          <label for="findQuery" class="tw-font-medium">Query</label>
          <p
            :data-invalid="queryWordCount > MAX_QUERY_WORD_COUNT"
            class="tw-mt-1 tw-text-xs tw-font-medium tw-text-gray-700 data-[invalid=true]:tw-text-red-600"
          >
            {{ queryWordCount }} of {{ MAX_QUERY_WORD_COUNT }} words
          </p>
        </div>
        <textarea id="findQuery" ref="queryEl" v-model="findQuery" required class="tw-rounded-md tw-border tw-p-2" />
      </div>
      <button
        type="submit"
        :disabled="state.status === 'loading'"
        class="tw-bg-green tw-b-r tw-col-start-2 tw-row-start-1 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
      >
        {{ isAskFindType ? "Ask" : "Search" }}
      </button>
      <button
        type="reset"
        class="tw-bg-gray tw-b-r tw-col-start-2 tw-row-start-2 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
        :disabled="findQuery.length < 1 || state.status === 'loading'"
        @click="resetFind"
      >
        Reset
      </button>
    </form>
    <div
      v-if="state.status === 'done' && state.results.type === 'ask'"
      class="tw-mx-3 tw-rounded-md tw-bg-blue-200/45 tw-p-2 tw-text-blue-900"
    >
      {{ state.results.response }}
    </div>
    <div class="tw-flex-1 tw-space-y-1.5 tw-overflow-y-auto tw-px-4">
      <Loading v-if="state.status === 'loading'" id="find-panel" message="Loading..." />
      <div v-else-if="state.status === 'error'" class="tw-grid tw-size-full tw-place-items-center">
        <div class="tw-max-w-sm tw-text-pretty tw-text-center tw-font-medium tw-text-red-700">
          <i class="fas fa-triangle-exclamation" />
          {{ state.message }}
        </div>
      </div>
      <SummaryItem
        v-for="item in filteredResults"
        v-else-if="filteredResults.length > 0"
        :key="item.uid"
        :snapshot="item"
        :is-selected="false"
        :display-header="allFilteredRecords"
        @click="() => goToCitation(item)"
      />
      <template v-else-if="state.status === 'done'">
        <div v-if="allFilteredRecords" class="tw-grid tw-size-full tw-place-items-center">
          <div class="tw-max-w-sm tw-text-pretty tw-text-center">
            The selected record does not contain any supporting or contrary evidence. Consider selecting a different
            record, or toggling the records filter switch
          </div>
        </div>
        <div v-else class="tw-flex tw-size-full tw-flex-col tw-items-center tw-justify-center tw-gap-2">
          <div class="tw-max-w-sm tw-text-pretty tw-text-center">
            There are additional search results in filtered-out records. Would you like to clear all record filters and
            search again?
          </div>
          <button type="button" class="tw-btn-md-gray" @click="resetFilters">Clear record filters</button>
        </div>
      </template>
    </div>
  </div>
</template>
